import { withAppServerSideProps } from '@app/lib/withAppServerSideProps';
import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import Divider from '@redbubble/design-system/src/Divider';
import Stack from '@redbubble/design-system/src/Stack';
import Text from '@redbubble/design-system/src/Text';
import { FormattedMessage } from '@redbubble/next/intl';
import { Seo } from '@redbubble/next/seo';
import {
  RSPageType,
  useRSPageView,
} from '@redbubble/next-provider/rudderstack';
import type { NextPage } from 'next';
import Image from 'next/legacy/image';
import React from 'react';
import apparel from './apparel.jpg';
import artBoards from './art-boards.jpg';
import casesAndSkins from './cases-and-skins.jpg';
import SearchForm from './components/SearchForm';
import step1 from './step-1.png';
import step2 from './step-2.png';
import step3 from './step-3.png';
import styles from './styles.module.scss';

export const About: NextPage = () => {
  useRSPageView(RSPageType.About);
  return (
    <div>
      <Seo title="About Us" />
      <Box display="flex" justifyContent="center">
        <Box
          paddingX="m"
          paddingY="xxxl"
          element="main"
          className={styles.pageMain}
        >
          <Stack paddingX="s" spacing="xl">
            <Stack>
              <Text type="display4">
                <FormattedMessage defaultMessage="Our Mission" />
              </Text>
              <Text type="display1">
                <FormattedMessage defaultMessage="Bringing more creativity into the world." />
              </Text>
            </Stack>

            <Box className={styles.videoWrapper}>
              <Box className={styles.videoInner}>
                <iframe
                  title="vimeo-player"
                  src="https://player.vimeo.com/video/195555159"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allowFullScreen
                />
              </Box>
            </Box>

            <Text>
              <FormattedMessage
                defaultMessage={`
            Redbubble was born in 2006 in Melbourne, Australia. The dream was
            simple. Give independent artists a meaningful new way to sell their
            creations. Today, we connect over 700,000 artists and designers
            across the planet with millions of passionate fans. A brave (and
            dare we say stylish) new world of self expression.
            `}
              />
            </Text>
          </Stack>
          <Stack spacing="xl" paddingY="xxl">
            <Box className={styles.imageSplit}>
              <Image
                sizes="50vw"
                alt=""
                placeholder="blur"
                layout="responsive"
                src={casesAndSkins}
              />
              <Image
                sizes="50vw"
                alt=""
                placeholder="blur"
                layout="responsive"
                src={artBoards}
              />
            </Box>
          </Stack>
          <Stack paddingX="s" spacing="xl">
            <Text type="display2">
              <FormattedMessage defaultMessage="Uncommon designs on awesome stuff" />
            </Text>
            <Text>
              <FormattedMessage
                defaultMessage={`
            A shirt with an evil cat. A phone case with a galloping donut. A
            tote bag with a star-surfing astronaut. Whatever your thing, you can
            get art you love on super well made products. Personal, original,
            and high quality? It’s a win-win-win.
            `}
              />
            </Text>
          </Stack>
          <Stack spacing="xl" paddingY="xxl">
            <Image
              alt=""
              placeholder="blur"
              layout="responsive"
              src={apparel}
            />
          </Stack>
          <Stack paddingX="s" spacing="xl">
            <Text type="display2">
              <FormattedMessage defaultMessage="Calling all artistic types." />
            </Text>
            <Text>
              <FormattedMessage
                defaultMessage={`
            Creative? Like money? Open a shop in minutes. For free. Just upload
            your art and designs and leave the rest to us. We arrange for all
            the printing (on over 60 quality products) and shipping to almost
            anywhere on earth. Ready? Let’s go.
            `}
              />
            </Text>
            <FormattedMessage defaultMessage="Sell your thing">
              {(text) => (
                <Button strong href="https://www.redbubble.com/about/selling">
                  {text}
                </Button>
              )}
            </FormattedMessage>
          </Stack>

          <Divider marginY="xxl" />

          <Stack paddingX="s" spacing="l">
            <Text type="display2">
              <FormattedMessage defaultMessage="How Redbubble works." />
            </Text>
            <Box className={styles.howItWorks}>
              <Box
                paddingY="l"
                display="flex"
                alignItems="center"
                flexDirection="column"
                className={styles.howItWorksEntry}
              >
                <Image alt="" placeholder="blur" src={step1} />
                <Text textAlign="center" display="block">
                  <FormattedMessage defaultMessage="Artists open a Redbubble shop and upload designs." />
                </Text>
              </Box>
              <Box
                paddingY="l"
                display="flex"
                alignItems="center"
                flexDirection="column"
                className={styles.howItWorksEntry}
              >
                <Image alt="" placeholder="blur" src={step2} />
                <Text textAlign="center" display="block">
                  <FormattedMessage defaultMessage="You find a design you love on a product you want." />
                </Text>
              </Box>
              <Box
                paddingY="l"
                display="flex"
                alignItems="center"
                flexDirection="column"
                className={styles.howItWorksEntry}
              >
                <Image alt="" placeholder="blur" src={step3} />
                <Text textAlign="center" display="block">
                  <FormattedMessage defaultMessage="Artists get paid and you get something sweet." />
                </Text>
              </Box>
            </Box>
          </Stack>

          <Divider marginY="xxl" />

          <Stack paddingX="s" spacing="xl">
            <Stack spacing="xxs" alignItems="center">
              <Text type="display2" display="block" textAlign="center">
                <FormattedMessage defaultMessage="Nice right? Now it’s time to find your thing." />
              </Text>
            </Stack>
            <SearchForm />
          </Stack>
        </Box>
      </Box>
    </div>
  );
};

export const getServerSideProps = withAppServerSideProps();

export default About;
